import React, { useEffect, useState } from 'react';
import { Check, ChevronLeft, Pencil, RotateCcw, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/components/Auth/AuthContext';
import { confirmDeletionByTyping } from '@/utils/deleteConfirmation';
import { formatPhoneForDisplay } from '@/utils/phone';
import { hasRequiredRoleAccess } from '@/utils/swaRole';
import { Button, DetailActionMenu, DetailFieldList, FormSection, StatusBadge } from '@/ui/components';
import { getInvoicePaymentState } from './invoiceStatus';
import '@/pages/dataGridFocus.css';

const WILSON_EMAIL = 'wd@linfinityeng.com';

function isHttpUrl(value?: string | null) {
  return /^https?:\/\//i.test(String(value || '').trim());
}

function isInspectionInvoice(invoice: any) {
  const invoiceType = String(invoice?.InvoiceType || '').trim().toLowerCase();
  const invoiceNumber = String(invoice?.InvoiceNumber || invoice?.InvoiceNo || '').trim();
  return invoiceType === 'inspection' || Boolean(invoice?.InspectionJobId) || /^INX\s+\d+/i.test(invoiceNumber);
}

function withPaymentState(invoice: any, paid: boolean, paidDate: string) {
  if (!invoice) return invoice;

  return {
    ...invoice,
    Status: paid ? 'Paid' : 'Sent',
    PaymentStatus: paid ? 'Paid' : 'Unpaid',
    PaidYN: paid ? 'Yes' : 'No',
    Paid: paid ? 'Yes' : 'No',
    PaidDate: paid ? (invoice.PaidDate || paidDate) : null,
    PaidOn: paid ? (invoice.PaidOn || paidDate) : null,
  };
}

type DetailSection = {
  title: string;
  icon: React.ReactNode;
  accentColor: string;
  fields: Array<{
    label: string;
    value: React.ReactNode;
    wrap?: boolean;
    emphasize?: boolean;
  }>;
};

const InvoiceDetail: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const currentUserEmail = (user?.userDetails || '').trim().toLowerCase();
  const isCivilInvoiceUser = currentUserEmail === WILSON_EMAIL;
  const canManageInvoiceMutations = hasRequiredRoleAccess(user?.userRoles, user?.userDetails, ['finance']);
  const [invoice, setInvoice] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [markingPaid, setMarkingPaid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const paymentState = getInvoicePaymentState(invoice || {});
  const isPaid = paymentState === 'Paid';
  const inspectionInvoice = isInspectionInvoice(invoice);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    loadInvoiceDetails();
  }, [isAuthenticated, user, invoiceId]);

  const loadInvoiceDetails = async () => {
    if (!invoiceId) return;

    setLoading(true);
    setError(null);
    setActionError(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const foundInvoice = await azureSqlService.getInvoiceById(invoiceId, { useCache: false });

      if (!foundInvoice) {
        setError('Invoice not found');
        return;
      }

      setInvoice(foundInvoice);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to load invoice: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const formatFieldValue = (value: any) => {
    if (value === null || value === undefined) return 'Not set';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string' && (value.includes('T') || value.includes('/'))) {
      try {
        const date = new Date(value);
        if (!Number.isNaN(date.getTime())) {
          return date.toLocaleDateString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          });
        }
      } catch {}
    }
    return String(value);
  };

  const formatAmount = (amount?: number | string) => {
    if (!amount && amount !== 0) return '$0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
    }).format(numAmount);
  };

  const detailSections: DetailSection[] = invoice ? [
    {
      title: 'Invoice Information',
      icon: '🧾',
      accentColor: 'var(--color-primary)',
      fields: [
        { label: 'Invoice Number', value: invoice.InvoiceNumber || 'Not set' },
        { label: inspectionInvoice ? 'Inspection Address' : 'Project Address', value: invoice.InspectionAddress || invoice.Description || 'Not set' },
        { label: 'Invoice Type', value: inspectionInvoice ? 'Inspection' : (invoice.InvoiceType || 'Standard') },
        { label: 'Status', value: invoice.Status ? <StatusBadge status={invoice.Status} /> : 'Not set' },
        { label: 'Payment State', value: <StatusBadge status={isPaid ? 'Paid' : 'Pending'}>{paymentState}</StatusBadge> },
        { label: 'Current Revision', value: invoice.CurrentRevisionNo ? `Revision ${invoice.CurrentRevisionNo}` : 'Revision 1' },
        { label: 'Latest Issued Revision', value: invoice.LatestIssuedRevisionNo ? `Revision ${invoice.LatestIssuedRevisionNo}${invoice.LatestIssuedRevisionStatus ? ` (${invoice.LatestIssuedRevisionStatus})` : ''}` : 'Not issued yet' },
        {
          label: 'Pending Amendment',
          value: invoice.HasPendingRevision
            ? `Yes. Draft revision ${invoice.CurrentRevisionNo || '1'} will replace revision ${invoice.LatestIssuedRevisionNo || '1'}.`
            : 'No',
          wrap: true,
        },
        { label: 'Created Date', value: formatFieldValue(invoice.Created) },
        { label: 'Due Date', value: formatFieldValue(invoice.DueDate) },
        { label: 'Invoice Details', value: invoice.InvoiceDetails || 'Not set' },
        { label: 'Amount (Excl GST)', value: formatAmount(invoice.TotalAmountExclGST) },
        { label: 'GST', value: formatAmount(invoice.GST) },
        { label: 'Total Receivable', value: formatAmount(invoice.TotalAmount), emphasize: true },
        { label: 'Reimbursement Item', value: invoice.ItemReim || 'Not set' },
        { label: 'Reimbursement', value: formatAmount(invoice.Reimbursement) },
        { label: 'Entity', value: invoice.Entity || 'Not set' },
        { label: 'Sender Display Name', value: invoice.SenderDisplayName || 'Not set', wrap: true },
        { label: 'Sender Address', value: invoice.SenderAddress || 'Not set', wrap: true },
        { label: 'Sender Mobile', value: formatPhoneForDisplay(invoice.SenderMobile) || 'Not set' },
        { label: 'Sender Email', value: invoice.SenderEmail || 'Not set', wrap: true },
        { label: 'Sender PayID', value: invoice.SenderPayId || 'Not set' },
        { label: 'Sender BSB', value: invoice.SenderBsb || 'Not set' },
        { label: 'Sender Account Number', value: invoice.SenderAccountNumber || 'Not set' },
        { label: 'Sender Account Name', value: invoice.SenderAccountName || 'Not set', wrap: true },
        { label: 'Paid Flag', value: invoice.PaidYN || 'No' },
        { label: 'Paid On', value: formatFieldValue(invoice.PaidDate) },
        {
          label: 'SharePoint File',
          value: invoice.PdfFileUrl
            ? (
              <div style={{ display: 'grid', gap: 6 }}>
                {isHttpUrl(invoice.PdfFileUrl) ? (
                  <a href={invoice.PdfFileUrl} target="_blank" rel="noreferrer" className="dgf-link">
                    Open saved SharePoint file
                  </a>
                ) : null}
                <span>{invoice.PdfFileUrl}</span>
              </div>
            )
            : 'Not filed yet',
          wrap: true,
        },
      ],
    },
    {
      title: 'Client Information',
      icon: '👤',
      accentColor: 'var(--color-info)',
      fields: [
        { label: 'Client Name', value: invoice.Client || 'Not set' },
        { label: 'Company Name', value: invoice.ClientCompany || 'Not set' },
        { label: 'ABN', value: invoice.ABN || 'Not set' },
        { label: 'Client Address', value: invoice.ClientAddress || 'Not set', wrap: true },
        { label: 'Mobile', value: formatPhoneForDisplay(invoice.ClientMobile) || 'Not set' },
        { label: 'Email', value: invoice.ClientEmail || 'Not set', wrap: true },
      ],
    },
  ] : [];

  const handleDeleteInvoice = async () => {
    if (!invoice || deleting || loading) return;
    if (!canManageInvoiceMutations) {
      setActionError('Only finance or admin users can delete invoices.');
      return;
    }

    const invoiceNumber = String(invoice.InvoiceNumber || invoice.InvoiceNo || invoice.InvoiceId || invoiceId || '').trim();
    const hasFiledSharePointFile = Boolean(String(invoice.PdfFileUrl || '').trim());
    const hasInspectionFolder = inspectionInvoice && Boolean(String(invoice.InspectionJobId || '').trim());
    const sharePointWarning = hasFiledSharePointFile
      ? 'This invoice has a saved SharePoint PDF. Delete that SharePoint invoice file separately after removing this invoice record.'
      : 'If this invoice has been filed to SharePoint, delete the SharePoint invoice file separately after removing this invoice record.';
    if (!invoiceNumber) {
      setActionError('Invoice number is required before deleting this invoice.');
      return;
    }

    if (!confirmDeletionByTyping({
      entityLabel: `invoice ${invoiceNumber}`,
      confirmationLabel: 'invoice number',
      identifiers: [invoiceNumber],
      softDelete: true,
      deleteModeMessage: 'This will soft-delete the invoice record in the database and permanently remove any linked draft blob file. SharePoint files will not be removed.',
      extraMessage: sharePointWarning,
    })) {
      return;
    }

    const deleteSharePointFolder = hasInspectionFolder
      ? window.confirm(`Delete the SharePoint folder for ${invoiceNumber} as well? Choose Cancel to keep the folder and only soft-delete the invoice.`)
      : false;

    setDeleting(true);
    setActionError(null);
    setActionNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      const result = await azureSqlService.deleteInvoice(invoice.InvoiceId || invoiceId, inspectionInvoice ? { deleteSharePointFolder } : undefined);
      setDeleting(false);
      navigate('/invoices', {
        state: {
          notice: `Deleted invoice ${invoiceNumber}.${result?.inspectionSharePointDelete?.deleted ? ' Deleted the inspection SharePoint folder.' : ''}${result?.inspectionSharePointDelete?.alreadyMissing ? ' Inspection SharePoint folder was already missing.' : ''}${result?.deletedDraftBlobCount ? ` Removed ${result.deletedDraftBlobCount} linked draft blob file${result.deletedDraftBlobCount === 1 ? '' : 's'}.` : ''}`,
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setActionError(message);
      setDeleting(false);
    }
  };

  const handleSetPaymentState = async (paid: boolean) => {
    if (!invoice || markingPaid || loading) return;
    if (!canManageInvoiceMutations) {
      setActionError('Only finance or admin users can update invoice payment state.');
      return;
    }

    setMarkingPaid(true);
    setActionError(null);
    setActionNotice(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.updateInvoice(invoice.InvoiceId || invoiceId, { paid });
      setInvoice((current: any) => withPaymentState(current, paid, new Date().toISOString()));
      setActionNotice(`Marked invoice ${invoice.InvoiceNumber || invoiceId} as ${paid ? 'paid' : 'unpaid'}.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setActionError(message);
    } finally {
      setMarkingPaid(false);
    }
  };

  return (
    <div className="dgf-page">
      <div className="dgf-detail-header">
        <div className="dgf-detail-heading">
          <button type="button" className="dgf-detail-back" aria-label="Back to invoices" onClick={() => navigate('/invoices')}>
            <ChevronLeft size={22} aria-hidden="true" />
          </button>
          <h1>
            {invoice
              ? `${isCivilInvoiceUser ? 'Civil Invoice' : 'Invoice'} ${invoice.InvoiceNumber || invoiceId}`
              : `${isCivilInvoiceUser ? 'Civil Invoice' : 'Invoice'} ${invoiceId || 'Details'}`}
          </h1>
        </div>
          {invoice ? (
            <div className="dgf-detail-actions dgf-detail-actions--primary">
              <Button
                size="sm"
                variant="secondary"
                aria-label="Amend in Workflow"
                iconBefore={<Pencil size={18} aria-hidden="true" />}
                onClick={() => navigate('/invoices/workflow', {
                  state: {
                    mode: 'amend',
                    invoiceId: invoice.InvoiceId || invoiceId,
                    invoiceNumber: invoice.InvoiceNumber,
                  },
                })}
                disabled={deleting}
              >
                Amend
              </Button>
              {canManageInvoiceMutations ? (
                <>
                  <Button
                    size="sm"
                    variant="secondary"
                    iconBefore={isPaid ? <RotateCcw size={18} aria-hidden="true" /> : <Check size={18} aria-hidden="true" />}
                    onClick={() => void handleSetPaymentState(!isPaid)}
                    disabled={deleting || markingPaid}
                  >
                    {markingPaid ? 'Saving...' : isPaid ? 'Mark Unpaid' : 'Mark Paid'}
                  </Button>
                  <DetailActionMenu label="More invoice actions" items={[{
                    label: deleting ? 'Deleting...' : 'Delete Invoice',
                    icon: <Trash2 size={18} aria-hidden="true" />,
                    onClick: () => void handleDeleteInvoice(),
                    disabled: deleting || markingPaid,
                    danger: true,
                  }]} />
                </>
              ) : null}
            </div>
          ) : null}
      </div>

      {loading && (
        <div className="dgf-page__content">
          <div className="dgf-state-card">
            <div className="dgf-state-card__icon">⏳</div>
            <div>Loading invoice details...</div>
          </div>
        </div>
      )}

      {error && (
        <div className="dgf-page__content">
          <div className="dgf-state-card">
            <div className="dgf-state-card__icon">❌</div>
            <div className="dgf-state-card__title">Error</div>
            <div className="dgf-notice dgf-notice--danger" style={{ marginTop: 16 }}>{error}</div>
            <Button onClick={() => loadInvoiceDetails()} variant="primary" style={{ marginTop: '16px' }}>
              🔄 Retry
            </Button>
          </div>
        </div>
      )}

      {!loading && !error && invoice && (
        <div className="dgf-page__content">
          {actionError ? (
            <div className="dgf-notice dgf-notice--danger">
              {actionError}
            </div>
          ) : null}
          {actionNotice ? (
            <div className="dgf-notice dgf-notice--success">
              {actionNotice}
            </div>
          ) : null}
          {detailSections.map((section) => (
            <FormSection
              key={section.title}
              title={section.title}
              icon={section.icon}
              accentColor={section.accentColor}
            >
              <DetailFieldList fields={section.fields} className="ui-detail-field-list--inline" />
            </FormSection>
          ))}
        </div>
      )}

      <div style={{ height: '80px' }} />
    </div>
  );
};

export default InvoiceDetail;
